import React from 'react'
import { Box, Link, Text, VStack, HStack, Spinner } from '@chakra-ui/react'
import { Check, Phone, Settings } from 'lucide-react'
import type { TicketRequest } from '@/types/ticketRequest'

export type DetailField = {
  key: string
  label: string
  value: React.ReactNode
}

export type DetailSection = {
  id: string
  title: string
  fields: DetailField[]
}

export type RequestProgressStep = {
  key: 'submitted' | 'acknowledged' | 'converted'
  label: string
  done: boolean
}

function extractedStr(fields: Record<string, unknown> | null | undefined, key: string): string {
  const v = fields?.[key]
  if (v == null || v === '') return ''
  return String(v).trim()
}

export function formatRequestDate(value: string | null | undefined): string {
  if (!value) return '–'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  const day = d.getDate()
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const month = months[d.getMonth()]
  const year = d.getFullYear()
  const hours = d.getHours()
  const minutes = d.getMinutes()
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const h12 = hours % 12 || 12
  const minStr = minutes < 10 ? `0${minutes}` : String(minutes)
  return `${day} ${month} ${year} - ${h12}:${minStr} ${ampm}`
}

export function formatRequestSource(value: string | null | undefined): string {
  if (!value) return '–'
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export function buildProgressSteps(row: TicketRequest): RequestProgressStep[] {
  const converted = !!(row.parent_ticket_id || row.parent_ticket_number)
  return [
    { key: 'submitted', label: 'Submitted', done: !!row.submitted_at },
    { key: 'acknowledged', label: 'Acknowledged', done: !!row.acknowledged_at },
    { key: 'converted', label: 'Converted', done: converted },
  ]
}

export function buildRequestDetailSections(
  row: TicketRequest,
  options?: {
    detailBody?: React.ReactNode
    isFetchingEmail?: boolean
  }
): DetailSection[] {
  const extractedFields = row.extracted_fields ?? null
  const serviceLabel = [row.service_parent, row.service_type].filter(Boolean).join(' / ') || '–'
  const attachments = row.attachment_metadata ?? []

  const sections: DetailSection[] = [
    {
      id: 'overview',
      title: 'Overview',
      fields: [
        { key: 'service', label: 'Service', value: serviceLabel },
        { key: 'source', label: 'Source', value: formatRequestSource(row.request_source) },
        { key: 'status', label: 'Status', value: row.ticket_status_label ?? '–' },
        { key: 'submitted_at', label: 'Submitted At', value: formatRequestDate(row.submitted_at ?? undefined) },
      ],
    },
    {
      id: 'request',
      title: 'Request Details',
      fields: [
        {
          key: 'description',
          label: 'Issue / Description',
          value: options?.isFetchingEmail ? (
            <HStack spacing={2}>
              <Spinner size="sm" />
              <Text fontSize="sm" color="gray.500">
                Loading email…
              </Text>
            </HStack>
          ) : (
            options?.detailBody ?? row.description ?? '–'
          ),
        },
        ...(row.requested_quantity != null
          ? [{ key: 'quantity', label: 'Devices Affected', value: String(row.requested_quantity) }]
          : []),
      ],
    },
    {
      id: 'contact',
      title: 'Contact',
      fields: [
        { key: 'contact_name', label: 'Contact Name', value: row.contact_name ?? '–' },
        { key: 'contact_email', label: 'Contact Email', value: row.contact_email ?? '–' },
        { key: 'contact_number', label: 'Contact Number', value: row.contact_number ?? '–' },
        ...(row.preferred_contact_at
          ? [{ key: 'preferred_contact', label: 'Preferred Contact', value: formatRequestDate(row.preferred_contact_at) }]
          : []),
      ],
    },
  ]

  const scheduleFields: DetailField[] = []
  if (row.preferred_schedule_at) {
    scheduleFields.push({ key: 'dispatch_from', label: 'Dispatch From', value: formatRequestDate(row.preferred_schedule_at) })
  }
  if (row.preferred_schedule_until || extractedStr(extractedFields, 'preferred_schedule_until')) {
    scheduleFields.push({
      key: 'dispatch_until',
      label: 'Dispatch Until',
      value: formatRequestDate(
        row.preferred_schedule_until ?? extractedStr(extractedFields, 'preferred_schedule_until')
      ),
    })
  }
  if (extractedStr(extractedFields, 'store_code')) {
    scheduleFields.push({
      key: 'store',
      label: 'Store / Site',
      value: [extractedStr(extractedFields, 'store_code'), extractedStr(extractedFields, 'location')]
        .filter(Boolean)
        .join(' — ') || '–',
    })
  }
  if (scheduleFields.length > 0) {
    sections.push({ id: 'schedule', title: 'Schedule & Site', fields: scheduleFields })
  }

  const triageFields: DetailField[] = []
  if (extractedStr(extractedFields, 'error_message')) {
    triageFields.push({ key: 'error_message', label: 'Error Message', value: extractedStr(extractedFields, 'error_message') })
  }
  if (extractedStr(extractedFields, 'work_impact')) {
    triageFields.push({ key: 'work_impact', label: 'Work Impact', value: extractedStr(extractedFields, 'work_impact') })
  }
  if (
    extractedStr(extractedFields, 'contact_availability') ||
    extractedStr(extractedFields, 'contact_availability_text')
  ) {
    triageFields.push({
      key: 'contact_availability',
      label: 'Contact Availability',
      value:
        extractedStr(extractedFields, 'contact_availability_text') ||
        extractedStr(extractedFields, 'contact_availability'),
    })
  }
  if (triageFields.length > 0) {
    sections.push({ id: 'triage', title: 'Additional Details', fields: triageFields })
  }

  if (attachments.length > 0) {
    sections.push({
      id: 'attachments',
      title: 'Attachments',
      fields: [
        {
          key: 'files',
          label: 'Files',
          value: (
            <VStack align="stretch" spacing={1}>
              {attachments.map((att, index) => {
                const href = att.file_url || att.path
                return (
                  <Link
                    key={`${att.file_name}-${index}`}
                    href={href}
                    isExternal
                    fontSize="sm"
                    color="blue.600"
                  >
                    {att.file_name}
                  </Link>
                )
              })}
            </VStack>
          ),
        },
      ],
    })
  }

  sections.push({
    id: 'timestamps',
    title: 'Timestamps',
    fields: [
      { key: 'created_at', label: 'Date Created', value: formatRequestDate(row.created_at) },
      { key: 'last_updated', label: 'Last Updated', value: row.last_updated_at_human ?? '–' },
    ],
  })

  return sections
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Text fontSize="xs" color="gray.500" fontWeight="600" mb={1}>
        {label}
      </Text>
      <Box fontSize="sm" lineHeight="short" whiteSpace="pre-wrap">
        {value ?? '–'}
      </Box>
    </Box>
  )
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={4} bg="white">
      <Text fontSize="sm" fontWeight="700" color="gray.700" mb={3}>
        {title}
      </Text>
      <VStack align="stretch" spacing={3}>
        {children}
      </VStack>
    </Box>
  )
}

export function RequestProgressStepper({ steps }: { steps: RequestProgressStep[] }) {
  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="lg" px={4} py={4} bg="white">
      <HStack justify="space-between" align="flex-start" position="relative">
        {steps.map((step, idx) => {
          const next = steps[idx + 1]
          const connectorGreen = step.done && next?.done
          const Icon = step.key === 'converted' ? Settings : step.done ? Check : Phone

          return (
            <Box key={step.key} flex="1" textAlign="center" position="relative">
              {idx !== steps.length - 1 && (
                <Box
                  position="absolute"
                  top="18px"
                  left="50%"
                  right="-50%"
                  h="2px"
                  bg={connectorGreen ? 'green.400' : 'gray.200'}
                />
              )}
              <Box
                mx="auto"
                w="36px"
                h="36px"
                borderRadius="full"
                border="2px solid"
                borderColor={step.done ? 'green.500' : 'gray.300'}
                bg={step.done ? 'green.500' : 'white'}
                display="flex"
                alignItems="center"
                justifyContent="center"
                position="relative"
                zIndex={1}
              >
                <Box as={Icon} boxSize={4} color={step.done ? 'white' : 'gray.400'} />
              </Box>
              <Text mt={2} fontSize="xs" fontWeight="700" color="gray.700">
                {step.label}
              </Text>
            </Box>
          )
        })}
      </HStack>
    </Box>
  )
}

type Props = {
  row: TicketRequest
  sections: DetailSection[]
  progressSteps: RequestProgressStep[]
  parentTicketLink?: React.ReactNode
  subject?: string | null
}

export function RequestDetailSections({ row, sections, progressSteps, parentTicketLink, subject }: Props) {
  return (
    <VStack align="stretch" spacing={4}>
      <RequestProgressStepper steps={progressSteps} />

      {(row.parent_ticket_number || parentTicketLink) && (
        <SectionCard title="Ticket">
          <DetailRow
            label="Ticket Number"
            value={
              parentTicketLink ?? (
                <Text fontFamily="mono" fontSize="sm">
                  {row.parent_ticket_number}
                </Text>
              )
            }
          />
          {row.parent_ticket_status ? (
            <DetailRow label="Ticket Status" value={formatRequestSource(row.parent_ticket_status)} />
          ) : null}
        </SectionCard>
      )}

      {subject ? (
        <SectionCard title="Subject">
          <DetailRow label="Email Subject" value={subject} />
        </SectionCard>
      ) : null}

      {sections.map((section) => (
        <SectionCard key={section.id} title={section.title}>
          {section.fields.map((field) => (
            <DetailRow key={field.key} label={field.label} value={field.value} />
          ))}
        </SectionCard>
      ))}
    </VStack>
  )
}
